"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, X, Check, AlertTriangle, CloudRain, Thermometer, MapPin } from "lucide-react";

type Alerta = {
  id: number;
  tipo: string;
  mensaje: string;
  severidad: string;
  fechaGenerada: string;
  resuelta: boolean;
  cultivo: string | null;
  zona: string | null;
  productor: string | null;
  actividadNombre: string | null;
};

const ICON_MAP: Record<string, typeof AlertTriangle> = {
  lluvia_alta: CloudRain,
  precipMM: CloudRain,
  tempMax: Thermometer,
  tempMin: Thermometer,
  siembra_sin_lote: MapPin,
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

export default function NotificationBell() {
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchAlertas = useCallback(async () => {
    try {
      const res = await fetch("/api/notificaciones?resuelta=false&limit=20");
      const data = await res.json();
      setAlertas(data.alertas || []);
      setTotal(data.total || 0);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchAlertas();
    const interval = setInterval(fetchAlertas, 60_000); // poll every minute
    return () => clearInterval(interval);
  }, [fetchAlertas]);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const resolver = async (ids: number[]) => {
    await fetch("/api/notificaciones", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, resuelta: true }),
    });
    setAlertas((prev) => prev.filter((a) => !ids.includes(a.id)));
    setTotal((prev) => Math.max(0, prev - ids.length));
  };

  const resolverTodas = () => {
    if (alertas.length === 0) return;
    resolver(alertas.map((a) => a.id));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-surface-hover transition-colors"
        aria-label="Notificaciones"
      >
        <Bell size={20} className="text-text-muted" />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 md:w-96 rounded-xl border border-border bg-surface shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-text">Alertas</span>
            <div className="flex items-center gap-2">
              {alertas.length > 0 && (
                <button
                  type="button"
                  onClick={resolverTodas}
                  className="text-[11px] text-accent hover:text-accent/80"
                >
                  Marcar todas leídas
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} className="text-text-muted hover:text-text p-2">
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {alertas.length === 0 ? (
              <div className="py-8 text-center text-sm text-text-muted">
                Sin alertas pendientes
              </div>
            ) : (
              alertas.map((a) => {
                const Icon = ICON_MAP[a.tipo] || AlertTriangle;
                const severityColor =
                  a.severidad === "critical"
                    ? "text-red-400"
                    : a.severidad === "warning"
                      ? "text-yellow-400"
                      : "text-blue-400";
                return (
                  <div
                    key={a.id}
                    className="flex items-start gap-3 px-4 py-3 border-b border-border/30 hover:bg-surface-hover/50 transition-colors"
                  >
                    <div className={`mt-0.5 ${severityColor}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-text leading-relaxed">
                        {a.mensaje}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-text-muted">
                        {a.cultivo && <span>{a.cultivo}</span>}
                        {a.zona && <span>· {a.zona}</span>}
                        {a.actividadNombre && <span>· {a.actividadNombre}</span>}
                        <span>· {timeAgo(a.fechaGenerada)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => resolver([a.id])}
                      className="p-1 text-text-muted hover:text-emerald-400 transition-colors shrink-0"
                      title="Marcar como resuelta"
                    >
                      <Check size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {total > alertas.length && (
            <div className="px-4 py-2 text-center text-[11px] text-text-muted border-t border-border">
              {total - alertas.length} alertas más
            </div>
          )}
        </div>
      )}
    </div>
  );
}
